import {
  AdTypeFilterGroup,
  AdTypePathTableRow,
  AmcPathingAttributionRow,
  AmcPathingCampaignPathRow,
  AmcPathingOccurrenceRow,
  AmcPathingTouchpointRow,
  CampaignAttributionTableRow,
  CampaignPathTableRow
} from './amc-pathing-analysis-types';

export const ATTRIBUTION_MODELS = ['First Touch', 'Last Touch', 'Linear', 'Position'];

// Heatmap cell colors are relative to the maximum value of a given metric across the whole
// table, so every column's heat scales independently of the others.
const getColumnMaxes = <T>(rows: T[], fields: (keyof T)[]): Record<string, number> =>
  Object.fromEntries(
    fields.map(field => [
      field,
      rows.length > 0 ? Math.max(...rows.map(row => Number(row[field]) || -1)) : -1
    ])
  );

export const filterTouchpointData = (
  data: AmcPathingTouchpointRow[],
  touchpoint: string
): AmcPathingTouchpointRow[] => data.filter(entry => entry.touchpoint === touchpoint);

export const calculateTotalPurchaseData = (
  data: AmcPathingTouchpointRow[]
): { adType: string; totalPurchases: number }[] => {
  const totalsByAdType = new Map<string, number>();
  data.forEach(entry => {
    totalsByAdType.set(
      entry.adType,
      (totalsByAdType.get(entry.adType) ?? 0) + entry.totalPurchases
    );
  });
  return [...totalsByAdType.entries()]
    .map(([adType, totalPurchases]) => ({ adType, totalPurchases }))
    .sort((a, b) => b.totalPurchases - a.totalPurchases);
};

export const filterCampaignTypes = (data: AmcPathingOccurrenceRow[]): AmcPathingOccurrenceRow[] =>
  [...data]
    .filter(entry => entry.dimensionLevel.toLocaleLowerCase() === 'campaign')
    .sort((a, b) => Math.abs(b.userThatPurchased) - Math.abs(a.userThatPurchased));

// Mirrors bpm-frontend: unlike `filterAdTypesToPathTable` below, this does not filter by
// `pathType` — every row in `campaignAndAdTypePathData` is included regardless of whether it's
// a "campaign path" or "ad_type path" entry. Preserved as-is for parity; flagged in the PR for
// follow-up.
export const formatCampaignAndAdTypeData = (
  data: AmcPathingCampaignPathRow[]
): CampaignPathTableRow[] => {
  const sorted = [...data].sort((a, b) => Math.abs(b.totalPurchases) - Math.abs(a.totalPurchases));
  const max = getColumnMaxes(sorted, [
    'reach',
    'impressions',
    'totalCost',
    'totalPurchases',
    'totalProductSales'
  ]);
  return sorted.map(entry => ({
    ...entry,
    heatMapValue_reach: entry.reach / max.reach,
    heatMapValue_impressions: entry.impressions / max.impressions,
    heatMapValue_totalCost: entry.totalCost / max.totalCost,
    heatMapValue_totalPurchases: entry.totalPurchases / max.totalPurchases,
    heatMapValue_totalProductSales: entry.totalProductSales / max.totalProductSales
  }));
};

const adTypeFromPath = (path: string): string[] =>
  path.split(',').map(segment => segment.split('-')[1]?.toLocaleLowerCase() ?? '');

const checkAgainstFilters = (
  data: (AmcPathingCampaignPathRow & { purchaseRate: number })[],
  filterKeys: string[],
  filterType: 'require' | 'exclude'
): (AmcPathingCampaignPathRow & { purchaseRate: number })[] => {
  if (filterKeys.length === 0) return data;
  return data.filter(entry => {
    const pathAdTypes = adTypeFromPath(entry.path);
    return filterType === 'require'
      ? filterKeys.every(key => pathAdTypes.includes(key))
      : !filterKeys.some(key => pathAdTypes.includes(key));
  });
};

const inclusionFilterKeys = (
  filtersData: AdTypeFilterGroup[],
  value: 'require' | 'exclude'
): string[] =>
  filtersData
    .flatMap(group => group.data)
    .filter(filter => filter.value === value)
    .map(filter => filter.name.toLocaleLowerCase());

export const filterAdTypes = (
  data: AmcPathingCampaignPathRow[],
  filtersData: AdTypeFilterGroup[]
): AdTypePathTableRow[] => {
  const adTypePathRows = data
    .filter(entry => entry.pathType?.toLocaleLowerCase() === 'ad_type path')
    .map(entry => ({
      ...entry,
      purchaseRate: Math.round((entry.totalPurchases / entry.impressions) * 100000) / 1000
    }));

  const requireKeys = inclusionFilterKeys(filtersData, 'require');
  const excludeKeys = inclusionFilterKeys(filtersData, 'exclude');
  const filtered = checkAgainstFilters(
    checkAgainstFilters(adTypePathRows, requireKeys, 'require'),
    excludeKeys,
    'exclude'
  );

  const max = getColumnMaxes(filtered, [
    'reach',
    'impressions',
    'userThatPurchased',
    'totalPurchases',
    'purchaseRate'
  ]);
  return filtered.map(entry => ({
    ...entry,
    heatMapValue_reach: entry.reach / max.reach,
    heatMapValue_impressions: entry.impressions / max.impressions,
    heatMapValue_userThatPurchased: entry.userThatPurchased / max.userThatPurchased,
    heatMapValue_totalPurchases: entry.totalPurchases / max.totalPurchases,
    heatMapValue_purchaseRate: entry.purchaseRate / max.purchaseRate
  }));
};

export const filterFirstTouchCampaignData = (
  data: AmcPathingAttributionRow[]
): AmcPathingAttributionRow[] =>
  [...data]
    .filter(entry => entry.attributionModel.toLocaleLowerCase() === 'first touch' && entry.campaign)
    .sort((a, b) => Math.abs(b.usersThatConverted) - Math.abs(a.usersThatConverted));

// Pivots one row per (campaign, attributionModel) into one row per campaign, with each
// attribution model's ROAS (totalProductSales / spend) as its own column. Not heatmapped.
export const groupAttributionData = (
  data: AmcPathingAttributionRow[]
): CampaignAttributionTableRow[] => {
  const rowsByCampaign = new Map<string, Record<string, number>>();
  data.forEach(entry => {
    if (!entry.campaign) return;
    const row = rowsByCampaign.get(entry.campaign) ?? {};
    row[entry.attributionModel] = entry.totalProductSales / entry.spend;
    rowsByCampaign.set(entry.campaign, row);
  });

  return [...rowsByCampaign.entries()].map(([campaign, row]) => ({
    campaign,
    'First Touch': row['First Touch'] ?? 0,
    'Last Touch': row['Last Touch'] ?? 0,
    Linear: row.Linear ?? 0,
    Position: row.Position ?? 0
  }));
};
